/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useState } from 'react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  createHttpLink,
} from '@apollo/client';
import { concat } from '@apollo/client/link/core';
import { v4 as uuidv4 } from 'uuid';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type {
  ContentServiceV1 as ContentService,
  ContentChangeListener,
} from '@volkswagen-onehub/audi-content-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { getGraphqlGatewayUrl } from './hooks/getGraphqlGatewayUrl';
import VehicleProvider from './components/VehicleProvider';
import i18n from '../i18n';
import ResetGlobalStyles from './styles';
import VehicleHeader from './components/VehicleHeader';
import ErrorBoundary from './components/ErrorBoundary';
import type { AppContent } from './content-mapping';
import { mapContent } from './content-mapping';
// import SEO from './components/SEO/SEO';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  contentService?: ContentService;
  trackingService?: TrackingServiceV2;
}

const superGraphLink = createHttpLink({
  uri: getGraphqlGatewayUrl,
});

const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-correlation-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const apolloClient = new ApolloClient({
  link: concat(correlationIdLink, superGraphLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

const FeatureApp: React.FC<FeatureAppProps> = ({ ...inherited }: FeatureAppProps) => {
  const { trackingService, contentService } = inherited;
  const [rawContent, setRawContent] = useState<AppContent | undefined>(
    contentService?.getContent() as AppContent | undefined,
  );
  const content = mapContent(rawContent);

  contentService?.onContentChange(setRawContent as ContentChangeListener);

  React.useEffect(() => {
    if (!trackingService) return;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, [trackingService]);

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <ThemeProvider>
          <ApolloProvider client={apolloClient}>
            <VehicleProvider>
              <div className="resetWrapper">
                {/* <SEO /> */}
                <VehicleHeader cta360View={content} />
              </div>
            </VehicleProvider>
          </ApolloProvider>
          <ResetGlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
